import {
  EventHandler,
  Listenable,
  EventId,
  EventType as GoogEventType,
  EventTarget,
} from 'goog:goog.events';
import Component from 'goog:goog.ui.Component';
import IdGenerator from 'goog:goog.ui.IdGenerator';
import { ComponentElement } from './component_element';
import { LitRenderer, EventType } from './lit_renderer';
import {
  html,
  TemplateResult,
  classMap,
  ifDefined,
  EmbeddableComponent,
  render,
} from './embeddable_component';

export { html, TemplateResult, classMap, ifDefined, render };

export type TemplateParam =
  | TemplateResult
  | LitComponent
  | Component
  | string
  | Node;

// Prevent an empty id prefix that allows a colon to start a generated id
IdGenerator.getInstance().setIdPrefix('d');

type Options = {
  renderSynchronously?: boolean;
};

/**
 * A view component that is a wrapper for a lit-html rendered element.
 * LitComponents can be embeded directly in templates returned by
 * parent LitComponents, and will be rendered and updated whenever
 * the parent is.
 */
export abstract class LitComponent extends EmbeddableComponent {
  private rootElement_: ComponentElement;
  private baseId_?: string;
  private eventHandler_?: EventHandler;
  private renderer_: LitRenderer;
  private isInDocument_ = false;
  private renderSynchronously: boolean;

  /**
   * @param opts
   * rootElement:
   * The HTMLElement that this component will render into.
   * This can be used to attach a LitComponent tree to the dom. If no
   * exisiting rootElement is provided, a new element will be created.
   */
  constructor(opts: Options = {}) {
    super();

    this.renderSynchronously = !!opts.renderSynchronously;

    this.rootElement_ = new ComponentElement(
      () => {
        this.renderer_.render();
        this.isInDocument_ = true;
        this.enterDocument();
      },
      () => {
        if (this.eventHandler_) {
          this.eventHandler_.removeAll();
        }
        this.isInDocument_ = false;
        this.exitDocument();
      }
    );

    const classNameOrNames = this.cssClassNames();
    if (classNameOrNames.length > 0) {
      const classNames = Array.isArray(classNameOrNames)
        ? classNameOrNames
        : [classNameOrNames];
      this.rootElement_.classList.add(...classNames);
    }

    this.renderer_ = new LitRenderer(
      this.rootElement_,
      () => this.renderTemplate(),
      {
        host: this,
      }
    );

    this.renderer_.listen(EventType.UPDATE, () => {
      this.updated();
      return true;
    });

    this.renderer_.listen(EventType.FIRST_UPDATE, () => {
      this.firstUpdated();
      return true;
    });
  }

  protected getEventHandler(): EventHandler {
    if (!this.eventHandler_) {
      this.eventHandler_ = new EventHandler(this);
    }
    return this.eventHandler_;
  }

  /**
   * Listens for events dispatched from an event target.
   *
   * @param src Event Source
   * @param type Event type to listen for or array of event types.
   * @param handler Callback function to be used as the listener or an object with handleEvent function.
   * The default behavior is to re-render the current component.
   * @param opt_options Options to be sent to addEventListener.
   * See https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener for more info.
   */
  protected listenTo(
    src: EventTarget | Listenable,
    type: string | string[] | EventId<any> | EventId<any>[],
    handler?: (a: any) => any,
    opt_options?: boolean | AddEventListenerOptions
  ): EventHandler<any> {
    handler = handler ? handler : this.requestUpdate;
    return this.getEventHandler().listen(src, type, handler, opt_options);
  }

  /**
   * Unlistens to events dispatched from an event target.
   *
   * @param src Event Source
   * @param type Event type to listen for or array of event types.
   * Defaults to the change events that are emitted from state objects
   * @param handler Callback function to be used as the listener or an object with handleEvent function.
   * The default behavior is to re-render the current component.
   * @param opt_options Options to be sent to addEventListener.
   * See https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener for more info.
   */
  protected unlistenTo(
    src: EventTarget | Listenable,
    type?: string | string[] | EventId<any> | EventId<any>[],
    handler?: (a: any) => any,
    opt_options?: boolean | AddEventListenerOptions
  ): EventHandler<any> {
    type = type ? type : GoogEventType.CHANGE;
    handler = handler ? handler : this.requestUpdate;
    return this.getEventHandler().unlisten(src, type, handler, opt_options);
  }

  /**
   * Defines the css classes that will be assigned to the
   * root element upon creation. Can return an empty string
   * or array if the component should have no classes
   */
  protected abstract cssClassNames(): string[] | string;

  /**
   * Returns the DOM element that this component is rendered into.
   * Also renders the component if it hasn't been rendered yet.
   */
  getRootElement(): ComponentElement {
    return this.rootElement_;
  }

  /**
   * Returns the DOM element that is a descendant of this component with the
   * given id
   */
  getElementById(id: string): Element | null {
    if (!id) {
      return null;
    }
    // Resolve against this component's root node rather than `document` so
    // the lookup still works when the component renders inside a shadow root,
    // where `document.getElementById` cannot see the element. `getElementById`
    // comes from `NonElementParentNode`, so the narrowing here is `Document |
    // DocumentFragment` — `ShadowRoot` is covered as a `DocumentFragment`,
    // while a detached subtree can root at a plain element, which is not.
    // Focus queries narrow to a different pair of types for the same reason;
    // see `activeElementInTreeOf`.
    const root = this.rootElement_.getRootNode();
    const el =
      root instanceof Document || root instanceof DocumentFragment
        ? root.getElementById(id)
        : null;
    if (!this.rootElement_.contains(el)) {
      return null;
    }
    return el;
  }

  /**
   * Returns the first DOM element that is a descendant of this component with the
   * given class
   */
  getElementByClassName(className: string): Element | null {
    if (!className) {
      return null;
    }
    return this.rootElement_.getElementsByClassName(className)[0] ?? null;
  }

  /**
   * Returns all DOM elements that are a descendant of this component with the
   * given class
   */
  getElementsByClassName(className: string): HTMLCollectionOf<Element> {
    return this.rootElement_.getElementsByClassName(className);
  }

  /** Returns true if this component has been rendered previously */
  isRendered() {
    return this.renderer_.isRendered();
  }

  /**
   * Returns a unique id for use as an element's id in the dom.
   * This id is guaranteed to be unique for each unique combination
   * of component instance and fragment, but will consistently generate
   * the same id for a specific component with the same fragment.
   */
  makeId(fragment: string): string {
    return `${this.baseId()}-${fragment}`;
  }

  /**
   * Returns a unique id for this instance of the component.
   * This can be used as an element id in the dom
   */
  baseId(): string {
    if (!this.baseId_) {
      this.baseId_ = IdGenerator.getInstance().getNextUniqueId();
    }

    return this.baseId_;
  }

  /** Manually start an update. */
  async requestUpdate(): Promise<void> {
    if (this.renderSynchronously) {
      this.renderer_.render();
      return;
    }

    await this.renderer_.requestUpdate();
  }

  isInDocument(): boolean {
    return this.isInDocument_;
  }

  /**
   * Lifecycle method that is called the first time the component is rendered.
   */
  protected firstUpdated() {}

  /**
   * Lifecycle method that is called each time the component is rendered.
   */
  protected updated() {}

  /**
   * Lifecycle method that is called each time the component is attached to the document.
   */
  protected enterDocument() {}

  /**
   * Lifecycle method that is called each time the component is removed from the document.
   */
  protected exitDocument() {}

  /**
   * Returns a template result that will be rendered into
   * this component's root element.
   */
  protected abstract renderTemplate(): TemplateResult;
}
